import {
  isRenderer
} from "./chunk-A6K3NLMV.js";
import {
  __require
} from "./chunk-DGUM43GV.js";

// src/utils/create-renderer-transport.ts
function createRendererTransport(channel) {
  const { ipcRenderer } = __require("electron");
  return {
    send: (event, ...args) => {
      ipcRenderer.send(`${channel}:${event}`, ...args);
    },
    sendSync: (event, ...args) => {
      return ipcRenderer.sendSync(`${channel}:${event}`, ...args);
    },
    invoke: async (event, ...args) => {
      return ipcRenderer.invoke(`${channel}:${event}`, ...args);
    },
    once: (event, callback) => {
      ipcRenderer.once(`${channel}:${event}`, (_event, ...args) => {
        callback(...args);
      });
    },
    on: (event, callback) => {
      ipcRenderer.on(`${channel}:${event}`, (_event, ...args) => {
        callback(...args);
      });
    },
    off: (event, callback) => {
      ipcRenderer.off(`${channel}:${event}`, callback);
    }
  };
}

// src/utils/get-oid.ts
function getOID() {
  if (isRenderer) {
    const pid = window.process?.pid ?? 1;
    const contextId = window.process?.contextId ?? "browser";
    return `oid:${btoa(`${pid}:${contextId}`)}`;
  }
  const raw = `pid:${process.pid}`;
  if (typeof Buffer === "undefined") {
    return `oid:${btoa(raw)}`;
  }
  return `oid:${Buffer.from(raw).toString("base64")}`;
}

// src/utils/is-main.ts
var isMain = typeof process !== "undefined" && process.type === "browser";

// src/utils/trusted-sender.ts
var PAGE_EXTENSION = ".html";
var nodePath = () => __require("path");
var cachedRendererRoots;
function getRendererRoots() {
  if (!cachedRendererRoots) {
    const { app } = __require("electron");
    cachedRendererRoots = rendererRootsFor(app.getAppPath());
  }
  return cachedRendererRoots;
}
function rendererRootsFor(appPath) {
  return {
    fileRoot: nodePath().join(appPath, "out", "renderer"),
    devOrigin: devOriginFromUrl(process.env.ELECTRON_RENDERER_URL)
  };
}
function devOriginFromUrl(devUrl) {
  if (!devUrl) return null;
  try {
    return new URL(devUrl).origin;
  } catch {
    console.warn(`[trusted-sender] ELECTRON_RENDERER_URL is not a URL (${devUrl}); no dev renderer page is trusted`);
    return null;
  }
}
function rendererPageFromUrl(rawUrl, roots = getRendererRoots()) {
  const path = nodePath();
  const { fileURLToPath } = __require("url");
  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.protocol === "file:") {
    let filePath;
    try {
      filePath = fileURLToPath(url);
    } catch {
      return null;
    }
    const relative = path.relative(roots.fileRoot, filePath);
    if (relative.length === 0 || relative.startsWith("..") || path.isAbsolute(relative)) return null;
  } else if (!roots.devOrigin || url.origin !== roots.devOrigin) {
    return null;
  }
  const base = path.posix.basename(url.pathname);
  if (!base.endsWith(PAGE_EXTENSION)) return null;
  const page = base.slice(0, -PAGE_EXTENSION.length);
  return page.length > 0 ? page : null;
}
function describeTrustedIpcSender(event, roots = getRendererRoots()) {
  if (!isMain) return null;
  const frame = event.senderFrame;
  const sender = event.sender;
  if (!frame || !sender || sender.isDestroyed()) return null;
  if (sender.getType() !== "window") return null;
  if (frame !== sender.mainFrame) return null;
  const page = rendererPageFromUrl(frame.url, roots);
  return page ? { page } : null;
}
function isTrustedIpcSender(event, roots) {
  return describeTrustedIpcSender(event, roots) !== null;
}

// src/utils/trusted-ipc-main.ts
function createTrustedIpcMain(getIpcMain, roots) {
  return {
    on(channel, listener) {
      getIpcMain().on(channel, (event, ...args) => {
        if (!isTrustedIpcSender(event, roots)) {
          console.warn(`[trustedIpcMain] dropped a message on "${channel}" from an untrusted sender`);
          return;
        }
        listener(event, ...args);
      });
    },
    handle(channel, handler) {
      getIpcMain().handle(channel, (event, ...args) => {
        if (!isTrustedIpcSender(event, roots)) {
          throw new Error("IPC invoke rejected: untrusted sender");
        }
        return handler(event, ...args);
      });
    }
  };
}
var trustedIpcMain = createTrustedIpcMain(() => __require("electron").ipcMain);

export {
  getOID,
  createRendererTransport,
  isMain,
  rendererRootsFor,
  rendererPageFromUrl,
  describeTrustedIpcSender,
  isTrustedIpcSender,
  createTrustedIpcMain,
  trustedIpcMain
};
//# sourceMappingURL=chunk-3HMZ6FL6.js.map